import type { Quest, QuestLog } from '../../../../shared/CharacterTypes';
import { QUEST_DATABASE, type QuestDefinition } from '../../../../shared/content/generated/quests.gen';
import { questObjectiveTargetName } from '../../../../shared/quests/questObjectiveDisplay';
import { questEndNpcDisplay } from '../../../../shared/quests/questEndNpcDisplay';
import { formatObjectiveTargetLabel, formatObjectiveProgress } from '../../../../config/ui/quest.config';

import { missingQuestPrerequisites, questPathStates, questPrerequisiteChain, type QuestStepState } from '../../../../shared/quests/questChain';
import { isQuestOffered } from '../../../../shared/quests/questOffer';

export interface JournalQuest {
  id: string;
  title: string;
  description: string;
  state: 'active' | 'ready' | 'completed' | 'available' | 'locked';
  featured?: boolean;
  /** Why a discovered chapter is still locked: open earlier quests, a level, or no one offering it. */
  lockedBy?: { quests: readonly string[]; level?: number; notOffered?: boolean };
  path?: ReadonlyArray<{ id: string; state: QuestStepState }>;
  live?: Quest;
  definition?: QuestDefinition;
}
export function questDefinition(id: string): QuestDefinition | undefined {
  return Object.prototype.hasOwnProperty.call(QUEST_DATABASE, id) ? QUEST_DATABASE[id] : undefined;
}

/**
 * Completion IDs take precedence over a stale active mirror; neither array is changed. A discovered chapter
 * is `available` when its giver would offer it now, by the server's own rules (NPCInteractionHandler's
 * offer filter: offered, level, earlier quests); otherwise `locked`, with what holds it. Without the
 * character's level, nothing above level 1 counts as available.
 */
export function journalEntries(log: QuestLog, discoveredIds: readonly string[] = [], playerLevel?: number): {
  active: JournalQuest[];
  completed: JournalQuest[];
  available: JournalQuest[];
  locked: JournalQuest[];
} {
  const completedIds = [...new Set(log.completed)];
  const done = new Set(completedIds);
  const activeQuests = [...new Map(log.active.filter(quest => !done.has(quest.id)).map(quest => [quest.id, quest])).values()];
  const activeIds = new Set(activeQuests.map(q => q.id));

  const active = activeQuests.map(live => {
    const definition = questDefinition(live.id);
    const path = definition?.featured
      ? questPathStates(questPrerequisiteChain(live.id).slice(0, -1), done, activeIds)
      : undefined;
    return {
      id: live.id,
      title: live.name && live.name !== live.id ? live.name : definition?.name ?? 'Unknown quest',
      description: live.description || definition?.description || 'No description is available.',
      state: live.completed ? 'ready' as const : 'active' as const,
      featured: definition?.featured,
      path,
      live,
      definition,
    };
  });

  const completed = completedIds.map(id => {
    const definition = questDefinition(id);
    return {
      id,
      title: definition?.name ?? 'Unknown quest',
      description: definition?.description ?? 'No description is available.',
      state: 'completed' as const,
      featured: definition?.featured,
      definition,
    };
  });

  const discovered = discoveredIds
    .filter(id => !done.has(id) && !activeIds.has(id))
    .map(id => {
      const definition = questDefinition(id);
      const chain = questPrerequisiteChain(id).slice(0, -1);
      const path = questPathStates(chain, done, activeIds);
      const quests = missingQuestPrerequisites(definition, prerequisite => done.has(prerequisite));
      const level = definition && definition.requiredLevel > (playerLevel ?? 1) ? definition.requiredLevel : undefined;
      const notOffered = !definition || !isQuestOffered(definition);
      const open = quests.length === 0 && level === undefined && !notOffered;
      return {
        id,
        title: definition?.name ?? id,
        description: definition?.description ?? 'A locked chapter awaits its prerequisites.',
        state: open ? 'available' as const : 'locked' as const,
        featured: definition?.featured ?? true,
        ...(open ? {} : { lockedBy: { quests, ...(level === undefined ? {} : { level }), ...(notOffered ? { notOffered } : {}) } }),
        path,
        definition,
      };
    });

  return {
    active,
    completed,
    available: discovered.filter(quest => quest.state === 'available'),
    locked: discovered.filter(quest => quest.state === 'locked'),
  };
}

export function journalObjectives(quest: Quest) {
  const objectives = quest.objectives?.length ? quest.objectives
    : [{ type: quest.type, targetId: quest.targetId, targetName: undefined, description: undefined,
      targetCount: quest.targetCount, currentCount: quest.currentCount }];
  const authored = questDefinition(quest.id)?.objectives;
  return objectives.map((objective, index) => {
    const type = objective.type ?? authored?.[index]?.type ?? quest.type;
    const current = objective.currentCount;
    const required = objective.targetCount;
    const knownRequirement = Number.isFinite(required) && required > 0;
    const knownCurrent = Number.isFinite(current);
    const target = objective.targetName ?? (type === 'level' && !knownRequirement ? 'Level objective'
      : questObjectiveTargetName(type, objective.targetId ?? authored?.[index]?.targetId ?? quest.targetId, required));
    return { label: knownRequirement ? formatObjectiveTargetLabel(type, target, required) : target,
      description: objective.description ?? authored?.[index]?.description,
      current, required, knownRequirement, knownCurrent, met: knownRequirement && knownCurrent && current >= required,
      progress: knownRequirement && knownCurrent ? formatObjectiveProgress(current, required, type)
        : `${knownCurrent ? current : 'Unknown'} / ${knownRequirement ? required : 'Unknown'}` };
  });
}
export function journalTurnIn(quest: Quest): string {
  return quest.endNpcName ?? questEndNpcDisplay(quest.id).name;
}
